import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, Card, Icon, Skeleton, Tag, useConfirm } from "@xinghai/ui";
import { useQuery } from "@tanstack/react-query";
import { studentApi } from "../../lib/api";
import type { ProfilePayload } from "../../lib/api";
import { qk } from "../../lib/queries";
import { DIALECT_LABELS, ORG_SHORT, TRAINING_LEVEL_LABELS } from "../../lib/constants";
import { useAuth, useLogout } from "../auth/useAuth";
import { CONSENT_ITEMS } from "../auth/consentText";
import { WorkAgreementStatus } from "../auth/WorkAgreementStatus";
import { ProfileSheet } from "./ProfileSheet";
import { ProfileCard } from "../account/ProfileCard";
import { ROLE_LABELS } from "../../app/roles";

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="xz-kv">
      <span className="gz-caption">{label}</span>
      <span>{value || "未填写"}</span>
    </div>
  );
}

/** docs/UX.md 5 — 个人页. */
export function MePage() {
  const { me, isLoading } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const logout = useLogout();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileQ = useQuery({
    queryKey: qk.profile,
    queryFn: () => studentApi.profile(),
    enabled: Boolean(me?.speaker),
    retry: false,
  });

  if (isLoading || !me) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={140} />
        <Skeleton variant="rect" height={180} />
      </div>
    );
  }

  const speaker = me.speaker;
  const consent = speaker?.consent;
  const signed = Boolean(consent?.train && consent?.display && consent?.commercial);
  /** `me/profile` is authoritative; the speaker record covers the first render. */
  const profile: ProfilePayload = profileQ.data?.profile ?? {
    dialect_l1: speaker?.dialect_l1 ?? [],
    hometown: null,
    training_level: speaker?.training_level ?? "",
    contact: null,
  };

  const onLogout = async () => {
    const ok = await confirm({ title: "退出登录？", confirmLabel: "退出", cancelLabel: "取消" });
    if (!ok) return;
    await logout.mutateAsync().catch(() => undefined);
    void navigate("/login", { replace: true });
  };

  return (
    <div className="xz-container xz-stack">
      <h1 className="gz-type-heading">我的</h1>

      <Card
        title="基本信息"
        padding="md"
        actions={
          <Button variant="secondary" onClick={() => setProfileOpen(true)}>
            <Icon id="ui-edit" size={20} /> 修改资料
          </Button>
        }
      >
        <Line label="姓名" value={me.user.display_name || me.user.username} />
        <Line label="用户名" value={me.user.username} />
        <Line label="编号" value={speaker?.speaker_code ?? "—"} />
        <Line label="班级" value={speaker?.class_id ?? ""} />
        <Line
          label="方言背景"
          value={(speaker?.dialect_l1 ?? []).map((d) => DIALECT_LABELS[d] ?? d).join("、")}
        />
        <Line label="家乡" value={profile.hometown ?? ""} />
        <Line
          label="训练情况"
          value={TRAINING_LEVEL_LABELS[profile.training_level] ?? profile.training_level}
        />
        <Line label="联系方式" value={profile.contact ?? ""} />
      </Card>

      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} initial={profile} />

      <ProfileCard />

      {(me.memberships?.length ?? 0) > 0 && (
        <Card title="我的班级和身份" padding="md">
          <ul className="xz-stack">
            {Object.entries(
              (me.memberships ?? []).reduce<Record<string, string[]>>((acc, row) => {
                const name = row.class_name || row.class_id;
                const label = ROLE_LABELS[row.role] ?? row.role;
                acc[name] = [...(acc[name] ?? []), label];
                return acc;
              }, {}),
            ).map(([name, labels]) => (
              <li key={name}>
                {name}
                <span className="gz-caption"> · {labels.join("、")}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="授权状态"
        subtitle={
          signed
            ? consent?.signed_at
              ? `授权已签署（${consent.signed_at}）`
              : "授权已签署"
            : "还没有签署授权"
        }
        padding="md"
        actions={
          <Tag tone={signed ? "success" : "warning"}>
            <Icon id={signed ? "ui-check-circle" : "ui-warning"} size={16} />
            {signed ? "已签署" : "未完成"}
          </Tag>
        }
      >
        <ul className="xz-consent__status">
          {CONSENT_ITEMS.map((c) => {
            const on = Boolean(consent?.[c.key]);
            return (
              <li key={c.key}>
                <Icon id={on ? "ui-check-circle" : "ui-close"} size={20} />
                <span>{c.title}</span>
                <span className="gz-caption">{on ? "已同意" : "未同意"}</span>
              </li>
            );
          })}
        </ul>
        {consent?.signed_at && <p className="gz-caption">签署时间：{consent.signed_at}</p>}
        <p className="gz-caption">
          如需撤回授权，请以书面形式联系{ORG_SHORT}；撤回不影响此前已进行的处理。
        </p>
        {/* staff who also hold an annotator role signed the work agreement */}
        <WorkAgreementStatus />
      </Card>

      <Card title="设置" padding="md">
        <div className="xz-account__links">
          <Link className="gz-button" to="/s/settings">
            <Icon id="ui-settings" size={20} /> 外观、自动提交、账号与安全
          </Link>
          <Button variant="secondary" onClick={() => void onLogout()} loading={logout.isPending}>
            <Icon id="ui-logout" size={20} /> 退出登录
          </Button>
        </div>
      </Card>
    </div>
  );
}
